import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Stack component for vertical layouts with consistent spacing
 */
const stackVariants = cva(
    "flex flex-col",
    {
        variants: {
            spacing: {
                xs: "gap-xs",
                sm: "gap-sm",
                md: "gap-md",
                lg: "gap-lg",
                xl: "gap-xl",
                "2xl": "gap-2xl",
                "3xl": "gap-3xl",
            },
            align: {
                start: "items-start",
                center: "items-center",
                end: "items-end",
                stretch: "items-stretch",
            },
            justify: {
                start: "justify-start",
                center: "justify-center",
                end: "justify-end",
                between: "justify-between",
                around: "justify-around",
            },
        },
        defaultVariants: {
            spacing: "md",
            align: "stretch",
        },
    }
)

const Stack = React.forwardRef(
    ({ className, spacing, align, justify, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(stackVariants({ spacing, align, justify, className }))}
                {...props}
            />
        )
    }
)
Stack.displayName = "Stack"

/**
 * Inline component for horizontal layouts with consistent spacing
 */
const inlineVariants = cva(
    "flex flex-row",
    {
        variants: {
            spacing: {
                xs: "gap-xs",
                sm: "gap-sm",
                md: "gap-md",
                lg: "gap-lg",
                xl: "gap-xl",
                "2xl": "gap-2xl",
            },
            align: {
                start: "items-start",
                center: "items-center",
                end: "items-end",
                stretch: "items-stretch",
                baseline: "items-baseline",
            },
            justify: {
                start: "justify-start",
                center: "justify-center",
                end: "justify-end",
                between: "justify-between",
                around: "justify-around",
            },
            wrap: {
                true: "flex-wrap",
                false: "flex-nowrap",
            },
        },
        defaultVariants: {
            spacing: "md",
            align: "center",
            wrap: false,
        },
    }
)

const Inline = React.forwardRef(
    ({ className, spacing, align, justify, wrap, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(inlineVariants({ spacing, align, justify, wrap, className }))}
                {...props}
            />
        )
    }
)
Inline.displayName = "Inline"

/**
 * Grid component for grid layouts
 */
const gridVariants = cva(
    "grid",
    {
        variants: {
            cols: {
                1: "grid-cols-1",
                2: "grid-cols-2",
                3: "grid-cols-3",
                4: "grid-cols-4",
                5: "grid-cols-5",
                6: "grid-cols-6",
                12: "grid-cols-12",
            },
            gap: {
                xs: "gap-xs",
                sm: "gap-sm",
                md: "gap-md",
                lg: "gap-lg",
                xl: "gap-xl",
                "2xl": "gap-2xl",
            },
        },
        defaultVariants: {
            cols: 1,
            gap: "md",
        },
    }
)

const Grid = React.forwardRef(
    ({ className, cols, gap, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(gridVariants({ cols, gap, className }))}
                {...props}
            />
        )
    }
)
Grid.displayName = "Grid"

/**
 * Spacer component for adding vertical or horizontal space
 */
const Spacer = React.forwardRef(
    ({ className, size = "md", direction = "vertical", ...props }, ref) => {
        const sizeClasses = {
            xs: direction === "vertical" ? "h-xs" : "w-xs",
            sm: direction === "vertical" ? "h-sm" : "w-sm",
            md: direction === "vertical" ? "h-md" : "w-md",
            lg: direction === "vertical" ? "h-lg" : "w-lg",
            xl: direction === "vertical" ? "h-xl" : "w-xl",
            "2xl": direction === "vertical" ? "h-2xl" : "w-2xl",
        }

        return (
            <div
                ref={ref}
                className={cn(sizeClasses[size], className)}
                aria-hidden="true"
                {...props}
            />
        )
    }
)
Spacer.displayName = "Spacer"

export { Stack, Inline, Grid, Spacer }
